import { LinkSourcesProvider } from "@nabble/sdk";
import { AppShell, ModuleActionsProvider } from "@nabble/ui";
import { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { SessionExpiryDialog } from "../components/SessionExpiryDialog";
import { useSessionKeepAlive } from "../hooks/useSessionKeepAlive";
import { useVisibleModules } from "../hooks/useVisibleModules";
import { PRIVACY_PATH, TERMS_PATH } from "../legal/legal";
import { paletteCommands } from "./commands";
import { currentTarget, moduleLanding, navTargets, sidebarNav } from "./navigation";
import { SectionHeader } from "./SectionHeader";
import type { ShellContext } from "./shellContext";
import { ShellOrganizationSwitcher } from "./ShellOrganizationSwitcher";
import { userRoleLabel } from "./userRoleLabel";

const SUITE_TITLE = "Nabble Suite";

const FOOTER_LINKS = [
  { label: "Privacy notice", href: PRIVACY_PATH },
  { label: "Terms of use", href: TERMS_PATH },
  { label: "nabble.cl", href: "https://nabble.cl", external: true },
];

/** The signed-in frame of every suite page: header, menu, content and footer. */
export function Shell(): React.JSX.Element {
  const { user, logout, sessionIdleMinutes, rememberedUntil } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // A "Keep me signed in" session isn't ended by inactivity: no keep-alive, no expiry warning.
  const session = useSessionKeepAlive(sessionIdleMinutes, !rememberedUntil);
  const visible = useVisibleModules();
  const { appModules } = visible;
  // What modules offer to link to, handed to every module without them knowing each other (docs/adr/0021).
  const linkSources = useMemo(() => visible.modules.flatMap((m) => m.linkSources ?? []), [visible.modules]);

  const targets = useMemo(() => navTargets(appModules), [appModules]);
  const current = currentTarget(targets, location.pathname);
  const currentModule = current?.module ? appModules.find((m) => m.label === current.module) : undefined;

  useEffect(() => {
    // The tab always shows just the suite name, whatever page is open.
    document.title = SUITE_TITLE;
  }, [current]);

  async function handleLogout(): Promise<void> {
    await logout();
    navigate("/login", { replace: true });
  }

  function openTarget(id: string): void {
    const mod = appModules.find((m) => m.id === id);
    const target = mod ? moduleLanding(mod) : targets.find((t) => t.id === id)?.path;
    if (target) navigate(target);
  }

  const context: ShellContext = visible;

  return (
    <AppShell
      nav={sidebarNav(appModules)}
      active={currentModule?.id ?? current?.id}
      onNavigate={openTarget}
      onHome={() => navigate("/")}
      product="suite"
      footer={{ links: FOOTER_LINKS, onNavigate: (href) => navigate(href) }}
      user={user ? { name: user.displayName, role: userRoleLabel(user), email: user.email } : undefined}
      sidebarFooter={user ? <ShellOrganizationSwitcher user={user} /> : null}
      onLogout={() => void handleLogout()}
      onProfile={() => navigate("/account/profile")}
      commands={paletteCommands({ appModules, targets, navigate, logout: () => void handleLogout() })}
      notifications={[]}
      viewKey={location.pathname}
    >
      <ModuleActionsProvider>
        <LinkSourcesProvider sources={linkSources}>
          {currentModule ? <SectionHeader module={currentModule} activeSectionId={current?.id} /> : null}
          <Outlet context={context} />
        </LinkSourcesProvider>
      </ModuleActionsProvider>
      <SessionExpiryDialog
        open={session.phase === "warning"}
        expiresAt={session.expiresAt}
        onStay={session.stayConnected}
        onLogout={() => void handleLogout()}
      />
    </AppShell>
  );
}
